// src/context/SiteContentContext.js
// - โหลด content จาก MySQL ทุกครั้งที่ mount
// - polling ทุก 30 วินาที เพื่อให้ user เห็นข้อมูลล่าสุดจาก Admin
// - section ที่ยังไม่มีใน MySQL → แสดง mockup default (merge ใน getSiteContent)

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  DEFAULT_SITE_CONTENT,
  getSiteContent,
  saveSiteContent,
} from '../services/contentService';

const POLL_INTERVAL_MS = 30000; // 30 วินาที

export const SiteContentContext = createContext(null);

export function SiteContentProvider({ children }) {
  // เริ่มต้นด้วย DEFAULT เพื่อป้องกัน flash ขณะรอ API
  const [siteContent, setSiteContent] = useState(DEFAULT_SITE_CONTENT);
  const [contentLoading, setContentLoading] = useState(true);

  // โหลดจาก MySQL → merge กับ default ทีละ section (ใน getSiteContent)
  const loadContent = useCallback(async () => {
    try {
      const content = await getSiteContent(); // ไม่ throw — คืน merged data เสมอ
      setSiteContent(content);
    } finally {
      setContentLoading(false);
    }
  }, []);

  // Mount: โหลดครั้งแรก + polling
  useEffect(() => {
    loadContent();

    const timer = setInterval(() => {
      // polling: ดึงข้อมูลล่าสุดจาก MySQL เงียบๆ (ไม่ trigger loading state)
      getSiteContent().then(setSiteContent).catch(() => {});
    }, POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [loadContent]);

  // setContent: บันทึกลง MySQL แล้วจึงอัปเดตหน้าจอด้วยผลจาก server
  // (ไม่ optimistic — ถ้าบันทึกไม่สำเร็จ หน้าเว็บต้องไม่แสดงข้อความที่ยังไม่ได้บันทึก)
  // ส่งเฉพาะ section ที่ต้องการบันทึกได้ — server รวมกับ section อื่นที่มีอยู่ให้เอง
  const setContent = useCallback(async (sections) => {
    const saved = await saveSiteContent(sections);
    setSiteContent(saved);
    return saved;
  }, []);

  const value = useMemo(
    () => ({
      siteContent,
      contentLoading,
      contentError: null,      // getSiteContent ไม่ throw แล้ว
      setContent,
      defaultContent: DEFAULT_SITE_CONTENT,
      reloadContent: loadContent,
    }),
    [siteContent, contentLoading, setContent, loadContent]
  );

  return (
    <SiteContentContext.Provider value={value}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  const context = useContext(SiteContentContext);
  if (!context) throw new Error('useSiteContent must be used within SiteContentProvider');
  return context;
}
